import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LinkField } from '@prismicio/client';
import { UserService } from 'auth-data-access';
import { LanguageService } from 'language';
import { LinkComponent } from 'link';
import { ProductListItem, ProductService } from 'product-shared';
import { PromotionBannerComponent } from 'promotion-banner';
import { ProductCardComponent } from 'sakura-ui';
import { ProductBoxBase } from '../product-box-base';
import { ProductBoxAnalyticsService } from './product-box-analytics.service';
import { ProductBoxCtaComponent } from './product-box-cta/product-box-cta.component';
import { ProductBoxCtaConfigService } from './product-box-cta-config.service';
import { ProductBoxDescriptionComponent } from './product-box-description/product-box-description.component';
import { ProductBoxFooterComponent } from './product-box-footer/product-box-footer.component';
import { ProductBoxHeaderComponent } from './product-box-header/product-box-header.component';
import { ProductBoxPricingComponent } from './product-box-pricing/product-box-pricing.component';

@Component({
  selector: 'lib-product-box',
  templateUrl: './product-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LinkComponent,
    PromotionBannerComponent,
    ProductCardComponent,
    ProductBoxHeaderComponent,
    ProductBoxDescriptionComponent,
    ProductBoxPricingComponent,
    ProductBoxCtaComponent,
    ProductBoxFooterComponent,
  ],
})
export class ProductBoxComponent extends ProductBoxBase {
  private productBoxCtaConfigService = inject(ProductBoxCtaConfigService);
  private productBoxAnalyticsService = inject(ProductBoxAnalyticsService);
  private productService = inject(ProductService);
  private languageService = inject(LanguageService);
  private userService = inject(UserService);
  private readonly userType = toSignal(this.userService.getUserType());
  public productItem = input<ProductListItem>(null);
  public countdownDate = input<string>();
  public override product = computed(() => this.productItem()?.product);
  public override promotion = computed(() => this.productItem()?.promotion);
  public lightMode = input(false);
  public isNavProduct = input(false);
  public showLearnMore = input(true);
  public typeOfPageShownOn = input('');
  public ctaCustomLabel = input('');
  public ctaCustomClasses = input('');
  public ctaCustomLink = input<LinkField>();
  public cssClass = input('');
  public productDetailsLink = computed<LinkField>(() => this.productService.productDetailsLink(this.product()));
  public ctaConfig = computed(() => {
    this.languageService.currentLanguage();

    const linkConfig = this.productBoxCtaConfigService.getCtaLink(
      this.product(),
      this.promotion(),
      this.lightMode(),
      this.typeOfPageShownOn(),
      this.ctaCustomLink(),
      this.productItem()?.lineCheckId
    );

    const styling = this.productBoxCtaConfigService.getCtaStyling(
      this.product(),
      this.promotion(),
      this.lightMode(),
      this.hasDiscountedCost(),
      this.ctaCustomLabel(),
      this.ctaCustomClasses(),
      this.translateService
    );

    return { ...linkConfig, ...styling };
  });
  public queryParams = computed(() => this.ctaConfig().queryParams);
  public ctaLink = computed(() => this.ctaConfig().link);
  public ctaLabel = computed(() => this.ctaConfig().label);
  public ctaClasses = computed(() => this.ctaConfig().classes);
  public contractDuration = computed(() => {
    if (!this.promotion()?.lockinLengths?.length && !this.product()?.lockin_length?.length) {
      return;
    }
    return this.productService.contractDuration(this.promotion()?.lockinLengths, this.product()?.lockin_length);
  });
  public discountPercentage = computed(() =>
    this.productService.getDiscountPercentage(this.product()?.monthly_cost, this.promotion()?.discountAmount)
  );
  public chipsLabelsKeys = computed(() =>
    this.productService.getChipsLabelsKeys(
      !!this.promotion(),
      this.hasDiscountedCost(),
      this.activationFee(),
      this.contractDuration()
    )
  );

  public onProductBoxClick() {
    this.productBoxAnalyticsService.trackProductBoxClick(
      this.product(),
      this.promotion(),
      this.ctaLink(),
      this.userType(),
      this.isNavProduct()
    );
  }
}
